import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type AuthState = {
  ready: boolean;
  email: string;
  isAdmin: boolean;
  isAdminTeam: boolean;
  isAdminLeadership: boolean;
};

const AuthCtx = createContext<AuthState>({ ready: false, email: '', isAdmin: false, isAdminLeadership: false, isAdminTeam: false });

export function useAuth(): AuthState {
  return useContext(AuthCtx);
}

const REFRESH_INTERVAL_MS = 15 * 60 * 1000;

function loginRedirect() {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  // domain_hint forces AAD to skip the account picker and go straight to the BMSS
  // tenant, preventing users with personal Microsoft accounts from accidentally
  // authenticating with the wrong identity and getting a 403.
  window.location.href = `/.auth/login/aad?domain_hint=bmss.com&post_login_redirect_uri=${returnTo}`;
}

// Hard cap on how long we wait for the silent login round-trip before giving up.
const SILENT_REAUTH_SETTLE_MS = 12 * 1000;

// Guard so overlapping interval ticks can't stack multiple silent-reauth iframes.
let reauthInFlight = false;

// Refresh the session WITHOUT navigating the top page — the "soft refresh". Runs the
// SWA/AAD login inside a hidden iframe: with a live Microsoft session, AAD completes
// SSO silently (no visible prompt, no top-page reload) and sets a fresh session
// cookie as a side effect. We then verify with /api/auth-check. Resolves true if the
// session is valid afterward. If AAD needs interaction, the framed flow can't
// complete and this resolves false, so the caller can fall back to an interactive
// redirect. The iframe lands on the static /silent-auth.html (excluded from the SPA
// navigation fallback, so it isn't rewritten to index.html).
function silentReauth(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.display = 'none';
    iframe.src =
      '/.auth/login/aad?domain_hint=bmss.com&post_login_redirect_uri=' +
      encodeURIComponent('/silent-auth.html');

    const verify = async () => {
      if (settled) return;
      settled = true;
      if (settleTimer) clearTimeout(settleTimer);
      clearTimeout(hardCap);
      let ok = false;
      try {
        const r = await fetch('/api/auth-check', { credentials: 'include', cache: 'no-store' });
        ok = r.ok; // 200 => re-authenticated
      } catch { /* verify failed -> treat as not ok */ }
      iframe.remove();
      resolve(ok);
    };

    // The login flow is a redirect chain; verify once it settles (no new load for a
    // short beat) or at the hard cap, whichever comes first.
    iframe.addEventListener('load', () => {
      if (settled) return;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(verify, 800);
    });
    const hardCap = setTimeout(verify, SILENT_REAUTH_SETTLE_MS);

    document.body.appendChild(iframe);
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ ready: false, email: '', isAdmin: false, isAdminLeadership: false, isAdminTeam: false });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (window.location.pathname === '/access-denied') return;

    fetch('/api/auth-check', { credentials: 'include', cache: 'no-store' })
      .then(async (r) => {
        if (r.status === 401) { loginRedirect(); return; }
        if (r.status === 403) { window.location.replace('/access-denied'); return; }
        if (!r.ok) throw new Error('auth-check failed');
        const { email, isAdmin, isAdminTeam, isAdminLeadership } = await r.json();
        setState({ ready: true, email, isAdmin, isAdminTeam: !!isAdminTeam, isAdminLeadership: !!isAdminLeadership });

        intervalRef.current = setInterval(async () => {
          try {
            const refresh = await fetch('/.auth/refresh', { credentials: 'include', cache: 'no-store' });
            if (refresh.ok) return; // token refreshed silently — no reload, nothing to do
            if (refresh.status === 401 && !reauthInFlight) {
              // Session couldn't be refreshed via /.auth/refresh. Re-authenticate
              // SILENTLY in a hidden iframe so the user's page/work is never
              // navigated; only fall back to an interactive redirect if that fails.
              reauthInFlight = true;
              const ok = await silentReauth();
              reauthInFlight = false;
              if (!ok) loginRedirect();
            }
          } catch { /* network error — leave session as-is */ }
        }, REFRESH_INTERVAL_MS);
      })
      .catch(() => {
        setState({ ready: true, email: '', isAdmin: false, isAdminLeadership: false, isAdminTeam: false });
      });

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const value = useMemo(() => state, [state]);

  // Don't render the app until we know auth state — prevents a flash of content
  // followed by a redirect, which users experience as a "blackout".
  // The /access-denied page is exempt so it can always render.
  if (!state.ready && window.location.pathname !== '/access-denied') {
    return null;
  }

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
